import customTheme from '@/constants/theme.custom';
import { Box, Text } from '@cmpsr/components';
import { PriceDataType } from '@/app/components/payment/components/method/payment-method-v2.controller';

interface MembershipSummaryProps {
  priceData: PriceDataType | null;
  isCouponVisible: boolean;
  showCoupon: () => void;
  coupon: string;
  setCoupon: (coupon: string) => void;
  couponValidationError: string | false | null;
  isSubmitting: boolean;
  getPrice: () => void;
  isSinglePaymentEnabled: boolean;
  isTrial: boolean;
  isPriceLoading: boolean;
  priceRecurring?: {
    interval?: string;
    intervalCount?: number;
    amount?: number;
  };
}

interface DiscountPriceViewProps {
  originalPrice: number;
  discountedPrice: number;
  savingsAmount: number;
}

interface DiscountPriceViewPropsExtended extends DiscountPriceViewProps {
  yearSuffix: string;
  productName: string;
  showPercentage?: boolean;
}

const DiscountPriceView = ({ originalPrice, discountedPrice, savingsAmount, yearSuffix, productName, showPercentage = false }: DiscountPriceViewPropsExtended) => (
  <Box display="flex" flexDirection="column" gap="4px" width="100%">
    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
      <Text
        fontWeight="bold"
        fontSize="18px"
        color="darkBlue"
        fontFamily={customTheme.typography.fonts.inter}
      >
        {productName}
      </Text>
      <Text
        fontSize="18px"
        fontWeight="bold"
        color="#0c6a58"
        fontFamily={customTheme.typography.fonts.inter}
      >
        ${discountedPrice}{yearSuffix}
      </Text>
    </Box>
    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
      <Text
        fontSize="16.91px"
        fontWeight="700"
        lineHeight="21px"
        color="#0C6A58"
        fontFamily={customTheme.typography.fonts.inter}
        textDecoration="line-through"
      >
        ${originalPrice}{yearSuffix}
      </Text>
      <Text
        fontSize="12px"
        fontWeight="bold"
        color="#0c6a58"
        fontFamily={customTheme.typography.fonts.inter}
        whiteSpace="nowrap"
      >
        {showPercentage ? `Save ${savingsAmount}%` : `Save $${savingsAmount}`}
      </Text>
    </Box>
  </Box>
);

const MembershipSummary = ({
  priceData,
  isSinglePaymentEnabled,
  isTrial,
  isPriceLoading,
  priceRecurring,
}: MembershipSummaryProps) => {
  // Price is loaded when we have data AND the API call is complete
  const isPriceLoaded = !isPriceLoading && priceData !== null;

  // Get the price values from priceData (only if loaded)
  const subtotal = priceData?.lines?.find(item => item.description === 'SUBTOTAL')?.amount ?? 0;
  const discount = priceData?.lines?.find(item => item.description === 'DISCOUNT')?.amount ?? 0;
  const tax = priceData?.lines?.find(item => item.description === 'TAX')?.amount ?? 0;

  // Calculate total price before tax (subtotal - discount)
  const totalPriceBeforeTax = subtotal - discount;
  const hasDiscount = discount > 0;

  // Show strikethrough for $499 when price is $119 with no discount
  const showStrikethrough = subtotal === 11900 && !hasDiscount;

  const yearSuffix = isSinglePaymentEnabled ? '' : '/year';
  const productName = isTrial ? '7-Day Free Trial' : (isSinglePaymentEnabled ? 'Catch Access' : 'Catch Membership');

  return (
    <Box
      position="relative"
      bg="white"
      borderRadius="16px"
      width="100%"
      border="2px solid"
      borderColor="#e6e6e6"
      overflow="visible"
      mt={hasDiscount || showStrikethrough || isTrial ? "12px" : "0"}
    >
      <Box p="26px">
        {!isPriceLoaded ? (
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Text
              fontWeight="bold"
              fontSize="18px"
              color="darkBlue"
              fontFamily={customTheme.typography.fonts.inter}
            >
              {productName}
            </Text>
            <Text
              fontSize="18px"
              fontWeight="bold"
              color="darkBlue"
              fontFamily={customTheme.typography.fonts.inter}
            >
              Loading...
            </Text>
          </Box>
        ) : isTrial ? (
          <Box display="flex" flexDirection="column" gap="4px" width="100%">
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Text
                fontWeight="bold"
                fontSize="18px"
                color="darkBlue"
                fontFamily={customTheme.typography.fonts.inter}
              >
                7-Day Free Trial
              </Text>
              <Text
                fontSize="18px"
                fontWeight="bold"
                color="#0c6a58"
                fontFamily={customTheme.typography.fonts.inter}
              >
                $0 today
              </Text>
            </Box>
            <Text
              fontSize="14px"
              fontWeight="400"
              color="#6B7280"
              fontFamily={customTheme.typography.fonts.inter}
            >
              {isPriceLoaded ? `Then $${((priceRecurring?.amount ?? 0) / 100).toFixed(0)}/year after trial ends` : 'Loading...'}
            </Text>
          </Box>
        ) : hasDiscount ? (
              <DiscountPriceView
                originalPrice={499}
                discountedPrice={(totalPriceBeforeTax / 100)}
                savingsAmount={Math.round(((499 - (totalPriceBeforeTax / 100)) / 499) * 100)}
                yearSuffix={yearSuffix}
                productName={productName}
                showPercentage={true}
              />
            ) : showStrikethrough ? (
              <DiscountPriceView
                originalPrice={499}
                discountedPrice={(subtotal / 100)}
                savingsAmount={75}
                yearSuffix={yearSuffix}
                productName={productName}
                showPercentage={true}
              />
        ) : (
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Text
              fontWeight="bold"
              fontSize="18px"
              color="darkBlue"
              fontFamily={customTheme.typography.fonts.inter}
            >
              {productName}
            </Text>
            <Text
              fontSize="18px"
              fontWeight="bold"
              color="darkBlue"
              fontFamily={customTheme.typography.fonts.inter}
            >
              ${(subtotal / 100).toFixed(0)}{yearSuffix}
            </Text>
          </Box>
        )}
      </Box>
      
      {(hasDiscount || showStrikethrough || isTrial) && (
        <Box
          position="absolute"
          top="-18px"
          left="24px"
          bg="#7cf1c7"
          borderRadius="8px"
          px="10px"
          py="4px"
        >
          <Text
            fontSize="10px"
            fontWeight="600"
            color="darkBlue"
            fontFamily={customTheme.typography.fonts.inter}
            whiteSpace="nowrap"
          >
            {isTrial ? 'Free Trial' : 'Early Access Discount'}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default MembershipSummary;
